import { faEllipsisV } from '@fortawesome/pro-regular-svg-icons/faEllipsisV';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SmartAnchor from '../../corecomponents/Anchor/SmartAnchor';
import {
  IAsAnchorOptions,
  IConfigurableLink
} from '../../models/configurableLink';
import THEME, { COLOR } from '../../styles/colors';
import {
  absoluteStyles,
  boxModelStyles,
  colorStyles,
  dropDownBoxShadowStyles,
  IBoxModelStyleProps
} from '../../styles/css';
import Font from '../../styles/font';
import IconButton from '../Button/IconButton';
import Icon from '../Icon/Icon';

export const StyledOverflowMenuWrapper = styled.div<{
  $isIntrinsiclyPositioned: boolean;
}>`
  position: ${props =>
    props.$isIntrinsiclyPositioned ? 'static' : 'relative'};
`;

const StyledTextButton = styled.button`
  ${colorStyles}
`;

export const StyledOverflowClickArea = styled.button`
  background: none;
  appearance: none;

  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  min-width: 14px;
  margin: auto;
`;

const StyledOverflowMenu = styled.div<any>`
  position: ${props => props.$position ?? 'absolute'};
  ${dropDownBoxShadowStyles}
  ${boxModelStyles}
  ${absoluteStyles}
  ${props => props.style};

  transform: ${props => props.$transform};
  background-color: ${COLOR.white};
  border-radius: 12px;
  padding: 8px 0;
  overflow-x: auto;
  z-index: 99;

  & > a,
  & > button {
    display: block;
    width: 100%;
    padding: 8px 16px;

    background: none;

    cursor: pointer;
    text-align: left;
    white-space: nowrap;
  }

  & svg {
    width: 16px;
  }

  & svg + span {
    margin-left: 8px;
  }
`;

const StyledDisabledOverflowMenuItem = styled.div`
  color: ${COLOR.neutral10};
  display: block;
  width: 100%;
  padding: 8px 16px;
  background: none;
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  cursor: default;
`;

const intrisiclyStyleProps = {
  /* The marginLeft value is arbitrary - i don't like it... 
    Consumer can override but it still stinks.
    The only reason i choose 24px was it was the size of the schedule edit button + a lil padding */
  $marginLeft: '24px',
  $transform: 'translate3d(-100%, 0, 0)'
};
const nonIntrisiclyStyleProps = {
  $right: 0,
  $top: '100%'
};

export interface IOverflowMenuProps extends IBoxModelStyleProps {
  children?: any;
  menuItems: IConfigurableLink[];
  $hoverColor?: string;
  alwaysShow?: boolean;
  isIntrinsiclyPositioned?: boolean;
}

const getMenuItems = (
  menuItems: IConfigurableLink[],
  styleProps,
  closeMenu
): React.ReactNode =>
  menuItems.map(item => {
    if (!item) return;

    const { icon, text, isDisabled, as, asOptions } = item;

    const row = (
      <>
        {icon && <Icon size={14} icon={icon} />}
        <span>{text}</span>
      </>
    );

    if (isDisabled) {
      return (
        <StyledDisabledOverflowMenuItem key={text}>
          {row}
        </StyledDisabledOverflowMenuItem>
      );
    }

    if (as === 'button') {
      const { onClick, ...buttonProps } = asOptions ?? {};
      return (
        <StyledTextButton
          key={text}
          {...styleProps}
          type="button"
          onClick={() => {
            onClick?.();
            closeMenu();
          }}
          {...buttonProps}
        >
          {row}
        </StyledTextButton>
      );
    }

    if (as == 'a') {
      const { anchorComponent, ...anchorProps } =
        (asOptions as IAsAnchorOptions) ?? {};
      const AnchorComponent = anchorComponent ?? SmartAnchor;
      return (
        <AnchorComponent key={text} {...styleProps} {...anchorProps}>
          {row}
        </AnchorComponent>
      );
    }
  });

// @ts-ignore
const DEFAULT_ICON = <IconButton size={18} icon={faEllipsisV} as="div" />;

const OverflowMenu: React.FC<IOverflowMenuProps> = ({
  children = DEFAULT_ICON,
  menuItems = [],
  $hoverColor = THEME.other.link,
  alwaysShow = false,
  /* 
  This control has two styling options based on isIntrinsiclyPositioned.
  isIntrinsiclyPositioned=true - will try to use non-left/right positoining. You may need to manually adjust the $marginLeft. It will have less conflict with overflows on parent elements.
  isIntrinsiclyPositioned=false - will try to use left/right positoining; it WILL line up perfect to the parent. It is more prone to conflicting with parents having overflow css values hidden.
  */
  isIntrinsiclyPositioned = true,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(alwaysShow || false);
  const ref = useRef<HTMLDivElement | null>(null);

  const closeMenu = useCallback(
    e => {
      if (!isOpen) return;
      if (ref.current?.contains(e?.target)) return;

      setIsOpen(alwaysShow || false);
    },
    [alwaysShow, isOpen]
  );

  useEffect(() => {
    document.addEventListener('click', closeMenu);

    return () => document.removeEventListener('click', closeMenu);
  }, [isOpen, closeMenu]);

  const overflowMenuStyleProps = isIntrinsiclyPositioned
    ? intrisiclyStyleProps
    : nonIntrisiclyStyleProps;

  let content;
  const isVisible = isOpen && menuItems.length > 0;
  if (isVisible) {
    const styleProps = {
      className: Font.f14_tall,
      $color: THEME.text.tertiary,
      $hoverColor
    };

    content = (
      <StyledOverflowMenu {...overflowMenuStyleProps} {...props}>
        {getMenuItems(menuItems, styleProps, closeMenu)}
      </StyledOverflowMenu>
    );
  }

  return (
    <StyledOverflowMenuWrapper
      $isIntrinsiclyPositioned={isIntrinsiclyPositioned}
      ref={ref}
    >
      <StyledOverflowClickArea type="button" onClick={() => setIsOpen(!isOpen)}>
        {children}
      </StyledOverflowClickArea>
      {content}
    </StyledOverflowMenuWrapper>
  );
};

export default OverflowMenu;
